import { motion } from "framer-motion";
import { ArrowLeft, Leaf, Sparkles, Target, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8f6f0] py-12 px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#2E4E3F] hover:text-[#1E2B22] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Waste Intelligence Engine</span>
        </button>

        <div className="bg-[#FDFBF7] rounded-3xl border border-[#E6E2D6] p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="flex items-center gap-4 mb-8">
            <Leaf className="w-12 h-12 text-[#2E4E3F]" />
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#1E2B22]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              About Us
            </h1>
          </div>

          <div className="space-y-8 text-[#4A5E51]">
            <section>
              <h2 className="text-3xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                What is Waste Intelligence Engine?
              </h2>
              <p className="text-lg leading-relaxed">
                Waste Intelligence Engine is an AI-powered research tool that helps businesses and individuals discover the hidden value in their waste. Simply type any waste material you generate—from e-waste to fruit peels—and our advanced AI will show you 4-6 research-backed innovations transforming that waste into valuable products.
              </p>
            </section>

            <div className="grid md:grid-cols-3 gap-6 my-12">
              <div className="bg-[#f8f6f0] p-6 rounded-2xl border border-[#E6E2D6]">
                <Sparkles className="w-8 h-8 text-[#B4654A] mb-4" />
                <h3 className="text-xl font-semibold text-[#2E4E3F] mb-2">AI-Powered Research</h3>
                <p className="text-sm leading-relaxed">
                  Utilizing Claude Sonnet 4.5, our AI analyzes global research, startups, and patents to deliver credible transformation ideas.
                </p>
              </div>

              <div className="bg-[#f8f6f0] p-6 rounded-2xl border border-[#E6E2D6]">
                <Target className="w-8 h-8 text-[#B4654A] mb-4" />
                <h3 className="text-xl font-semibold text-[#2E4E3F] mb-2">Circular Economy</h3>
                <p className="text-sm leading-relaxed">
                  Every search result promotes circular economy principles, showing how waste can become a resource.
                </p>
              </div>

              <div className="bg-[#f8f6f0] p-6 rounded-2xl border border-[#E6E2D6]">
                <Users className="w-8 h-8 text-[#B4654A] mb-4" />
                <h3 className="text-xl font-semibold text-[#2E4E3F] mb-2">Partnership Insights</h3>
                <p className="text-sm leading-relaxed">
                  Each result includes "The Kaagazz Connection"—exploring partnership opportunities in waste transformation.
                </p>
              </div>
            </div>

            <section>
              <h2 className="text-3xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Our Partnership with Kaagazz
              </h2>
              <p className="text-lg leading-relaxed mb-4">
                Waste Intelligence Engine is powered by <a href="https://ecokaagazz.com" target="_blank" rel="noopener noreferrer" className="text-[#B4654A] hover:underline font-medium">Kaagazz</a>, a pioneering eco-friendly paper products brand that's redefining sustainability in India.
              </p>
              <p className="text-lg leading-relaxed">
                Kaagazz transforms agricultural waste—banana peels, rice husks, sugarcane bagasse—into beautiful, sustainable stationery and paper products. Their mission aligns perfectly with ours: proving that waste is not an end, but a beginning.
              </p>
            </section>

            <section className="bg-[#2E4E3F] -mx-8 md:-mx-12 px-8 md:px-12 py-8 rounded-3xl text-[#FDFBF7]">
              <h2 className="text-3xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Our Mission
              </h2>
              <p className="text-lg leading-relaxed opacity-90">
                To democratize knowledge about waste transformation and circular economy solutions. We believe every piece of waste has potential, and by connecting waste generators with proven innovations, we're accelerating India's—and the world's—transition to a circular economy.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                How It Works
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2E4E3F] mb-2">Enter Your Waste Type</h3>
                    <p className="leading-relaxed">Type any waste material you generate—industrial, agricultural, household, or electronic.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2E4E3F] mb-2">AI Analyzes Global Research</h3>
                    <p className="leading-relaxed">Claude Sonnet 4.5 scans its knowledge base of research papers, startups, patents, and innovations worldwide.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2E4E3F] mb-2">Get Transformation Ideas</h3>
                    <p className="leading-relaxed">Receive 4-6 credible ideas showing what your waste can become—with sources, categories, and partnership insights.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2E4E3F] mb-2">Discover Partnership Opportunities</h3>
                    <p className="leading-relaxed">Explore "The Kaagazz Connection" to see how your waste could become raw material for eco-friendly products.</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Who Should Use This?
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#f8f6f0] rounded-xl border border-[#E6E2D6]">
                  <h3 className="font-semibold text-[#2E4E3F] mb-2">🏭 Businesses & Manufacturers</h3>
                  <p className="text-sm leading-relaxed">Find innovative ways to monetize industrial waste and reduce disposal costs.</p>
                </div>

                <div className="p-4 bg-[#f8f6f0] rounded-xl border border-[#E6E2D6]">
                  <h3 className="font-semibold text-[#2E4E3F] mb-2">🌾 Farmers & Agriculture</h3>
                  <p className="text-sm leading-relaxed">Discover value in crop residues, husks, and agricultural by-products.</p>
                </div>

                <div className="p-4 bg-[#f8f6f0] rounded-xl border border-[#E6E2D6]">
                  <h3 className="font-semibold text-[#2E4E3F] mb-2">♻️ Sustainability Professionals</h3>
                  <p className="text-sm leading-relaxed">Research circular economy solutions and waste transformation opportunities.</p>
                </div>

                <div className="p-4 bg-[#f8f6f0] rounded-xl border border-[#E6E2D6]">
                  <h3 className="font-semibold text-[#2E4E3F] mb-2">🏡 Conscious Consumers</h3>
                  <p className="text-sm leading-relaxed">Learn what happens to household waste and support circular economy initiatives.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Technology
              </h2>
              <p className="text-lg leading-relaxed mb-4">
                We leverage cutting-edge AI technology to deliver accurate, research-backed insights:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-lg">
                <li><strong>Claude Sonnet 4.5</strong> by Anthropic for intelligent analysis</li>
                <li><strong>React</strong> for a beautiful, responsive user experience</li>
                <li><strong>FastAPI</strong> for lightning-fast backend processing</li>
                <li><strong>MongoDB</strong> for efficient data management</li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Contact & Partnerships
              </h2>
              <p className="text-lg leading-relaxed mb-4">
                Interested in partnering with us or learning more about waste transformation opportunities? 
              </p>
              <div className="p-6 bg-[#f8f6f0] rounded-2xl border border-[#E6E2D6]">
                <p className="font-medium text-lg mb-2">Visit Kaagazz</p>
                <a 
                  href="https://ecokaagazz.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#B4654A] hover:underline text-lg"
                >
                  ecokaagazz.com →
                </a>
              </div>
            </section>

            <section className="text-center pt-8 border-t border-[#E6E2D6]">
              <p className="text-sm text-[#4A5E51] italic">
                "Every piece of waste is a raw material waiting to be discovered."
              </p>
            </section>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default About;